import React from 'react';
import { 
  ConnectAccountOnboarding, 
  ConnectAccountManagement,
  ConnectPayments,
  ConnectPayouts
} from '@stripe/react-connect-js';
import { Box, Alert, Typography } from '@mui/material';

// Styled container for all embedded components
const EmbeddedContainer = ({ children, minHeight = '400px', ...props }) => (
  <Box
    sx={{
      border: '1px solid #e0e0e0',
      borderRadius: 2,
      p: 2,
      minHeight,
      backgroundColor: '#fafafa',
      '& iframe': {
        borderRadius: 1,
      },
      ...props.sx
    }}
    {...props}
  >
    {children}
  </Box>
);

// Account Onboarding Component
export const EmbeddedAccountOnboarding = ({ 
  accountId, 
  onExit, 
  onLoadError,
  ...props 
}) => {
  if (!accountId) {
    return (
      <EmbeddedContainer>
        <Alert severity="warning">
          Account ID is required for onboarding
        </Alert>
      </EmbeddedContainer>
    );
  }

  return (
    <EmbeddedContainer {...props}>
      <ConnectAccountOnboarding
        connectedAccountId={accountId}
        onExit={onExit}
        onLoadError={(error) => {
          console.error('Account onboarding error:', error);
          if (onLoadError) onLoadError(error);
        }}
      />
    </EmbeddedContainer>
  );
};

// Account Management Component
export const EmbeddedAccountManagement = ({ 
  accountId, 
  onLoadError,
  ...props 
}) => {
  if (!accountId) {
    return (
      <EmbeddedContainer>
        <Alert severity="warning">
          Account ID is required for account management
        </Alert>
      </EmbeddedContainer>
    );
  }

  return (
    <EmbeddedContainer {...props}>
      <ConnectAccountManagement
        connectedAccountId={accountId}
        onLoadError={(error) => {
          console.error('Account management error:', error);
          if (onLoadError) onLoadError(error);
        }}
      />
    </EmbeddedContainer>
  );
};

// Payments Component
export const EmbeddedPayments = ({ 
  accountId, 
  onLoadError,
  ...props 
}) => {
  if (!accountId) {
    return (
      <EmbeddedContainer>
        <Alert severity="warning">
          Account ID is required for payments view
        </Alert>
      </EmbeddedContainer>
    );
  }

  return (
    <EmbeddedContainer {...props}>
      <ConnectPayments
        connectedAccountId={accountId}
        onLoadError={(error) => {
          console.error('Payments error:', error);
          if (onLoadError) onLoadError(error);
        }}
      />
    </EmbeddedContainer>
  );
};

// Payouts Component
export const EmbeddedPayouts = ({ 
  accountId, 
  onLoadError,
  ...props 
}) => {
  if (!accountId) {
    return (
      <EmbeddedContainer>
        <Alert severity="warning">
          Account ID is required for payouts view
        </Alert>
      </EmbeddedContainer>
    );
  }

  return (
    <EmbeddedContainer {...props}>
      <ConnectPayouts
        connectedAccountId={accountId}
        onLoadError={(error) => {
          console.error('Payouts error:', error);
          if (onLoadError) onLoadError(error);
        }}
      />
    </EmbeddedContainer>
  );
};

// Quick Setup Component - combines onboarding status with management
export const EmbeddedQuickSetup = ({ 
  accountId, 
  accountStatus = 'pending',
  onStatusChange,
  onLoadError,
  ...props 
}) => {
  const isComplete = accountStatus === 'active' || accountStatus === 'complete';

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {isComplete ? 'Account Management' : 'Complete Your Setup'}
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        {isComplete 
          ? 'Update your account information and settings.'
          : 'Complete your account setup to start receiving payments.'
        }
      </Typography>
      
      {isComplete ? (
        <EmbeddedAccountManagement
          accountId={accountId}
          onLoadError={onLoadError}
          {...props}
        />
      ) : (
        <EmbeddedAccountOnboarding
          accountId={accountId}
          onExit={() => {
            console.log('Onboarding completed');
            if (onStatusChange) onStatusChange('active');
          }}
          onLoadError={onLoadError}
          {...props}
        />
      )}
    </Box>
  );
};

export default {
  EmbeddedAccountOnboarding,
  EmbeddedAccountManagement,
  EmbeddedPayments,
  EmbeddedPayouts,
  EmbeddedQuickSetup
};